import type { WorkOrderStatusValue } from './statuses';

/**
 * 🔒 One job, many visits (decision 1901; T1157, T1242). A visit is a
 * `WorkOrderVisit` row under the Work Order with its own window, people and
 * state, and exactly one of its people is the lead. These are the visit's
 * rules that need no database: its label, its states, the job's roll-up and
 * the refusals a booking and a cancel meet.
 */

export const WORK_ORDER_VISIT_STATUSES = ['Scheduled', 'Dispatched', 'InProgress', 'Complete', 'Cancelled'] as const;
export type WorkOrderVisitStatus = (typeof WORK_ORDER_VISIT_STATUSES)[number];

/** The words a visit's state reads as; Could not complete is an outcome on a Complete visit, never a sixth state. */
export const WORK_ORDER_VISIT_STATUS_LABELS: Readonly<Record<WorkOrderVisitStatus, string>> = {
  Scheduled: 'Scheduled',
  Dispatched: 'Dispatched',
  InProgress: 'In Progress',
  Complete: 'Complete',
  Cancelled: 'Canceled',
};

/** A visit is drawn V1, V2 and on, in the order it was booked; a cancelled visit keeps its number. */
export function visitLabel(visitNumber: number): string {
  return `V${visitNumber}`;
}

/** Whether a visit still stands to be worked: Scheduled, Dispatched or In Progress. */
export function visitIsOpen(status: WorkOrderVisitStatus): boolean {
  return status === 'Scheduled' || status === 'Dispatched' || status === 'InProgress';
}

const ROLL_RANK: Partial<Record<WorkOrderStatusValue, number>> = { Draft: 0, Scheduled: 1, Dispatched: 2, InProgress: 3 };
const VISIT_ROLLS_TO: Partial<Record<WorkOrderVisitStatus, WorkOrderStatusValue>> = { Scheduled: 'Scheduled', Dispatched: 'Dispatched', InProgress: 'InProgress' };

/**
 * 🔒 The job's status rolls forward from its visits and never back (decision
 * 1901). A booked visit reads the job Scheduled, a dispatched one Dispatched and
 * a started one In Progress, each only where the job stands behind it. Answers
 * the status the job moves to, or null where it moves nowhere. A job past In
 * Progress, and a cancelled or completed visit, move nothing.
 */
export function jobStatusFromVisits(jobStatus: WorkOrderStatusValue, visits: readonly WorkOrderVisitStatus[]): WorkOrderStatusValue | null {
  const current = ROLL_RANK[jobStatus];
  if (current === undefined) return null;
  let target: WorkOrderStatusValue | null = null;
  for (const status of visits) {
    const rolled = VISIT_ROLLS_TO[status];
    if (rolled && ROLL_RANK[rolled]! > (target === null ? current : ROLL_RANK[target]!)) target = rolled;
  }
  return target;
}

/**
 * 🔒 A visit's crew and its one lead (decision 1901). The lead is the booking
 * person's choice among the workers going, and the first of them where nobody
 * chose. Answers the lead, or a refusal naming why; a visit booked with no
 * worker carries no lead until one is named.
 */
export function visitLeadOf(employmentIds: readonly string[], chosenLead: string | null | undefined): { lead: string | null; problem: string | null } {
  if (employmentIds.length === 0) return { lead: null, problem: chosenLead ? 'a visit with nobody going has no lead; name the workers going first' : null };
  if (!chosenLead) return { lead: employmentIds[0]!, problem: null };
  if (!employmentIds.includes(chosenLead)) return { lead: null, problem: 'the lead is one of the workers going on the visit' };
  return { lead: chosenLead, problem: null };
}

/** 🔒 Cancel on a visit asks a reason, and a visit already Complete or Cancelled is not cancelled again (decision 1901). */
export function visitCancelProblem(status: WorkOrderVisitStatus, reason: string | null | undefined): string | null {
  if (!visitIsOpen(status)) return `a ${WORK_ORDER_VISIT_STATUS_LABELS[status]} visit is not canceled`;
  if (!reason || !reason.trim()) return 'canceling a visit records why';
  return null;
}

/** 🔒 Book Another Visit stands on a job already booked and not yet finished (decision 1901); the first visit is booked by Schedule. */
export function bookAnotherVisitProblem(jobStatus: WorkOrderStatusValue): string | null {
  if (jobStatus === 'Draft') return 'the first visit is booked with Schedule';
  if (jobStatus === 'Scheduled' || jobStatus === 'Dispatched' || jobStatus === 'InProgress') return null;
  return `a ${jobStatus} job books no further visit`;
}

/**
 * 🔒 What Edit Visit may change on a visit in each state (decision 2813). A
 * Scheduled or Dispatched visit moves its window and changes its crew; a
 * started one changes its crew only, because its window is where it happened;
 * a Complete or Cancelled visit is not edited. A dispatched or started visit
 * keeps at least one worker, since its lead completes it.
 */
export function editVisitProblem(status: WorkOrderVisitStatus, change: { windowMoved: boolean; crewSize: number }): string | null {
  if (!visitIsOpen(status)) return `a ${WORK_ORDER_VISIT_STATUS_LABELS[status]} visit is not edited`;
  if (status === 'InProgress' && change.windowMoved) return 'a started visit keeps its window; only its crew changes';
  if (status !== 'Scheduled' && change.crewSize === 0) return 'a dispatched or started visit keeps at least one worker, its lead';
  return null;
}

/** 🔒 Whether Edit Visit moves the window of a visit in this state, or shows it read-only (decision 2813). */
export function visitWindowEditable(status: WorkOrderVisitStatus): boolean {
  return status === 'Scheduled' || status === 'Dispatched';
}

/**
 * 🔒 A visit Can move, the default, or is Fixed with a typed reason (decision
 * 3100; T2186). The state is one column, `fixedReason?`: empty reads Can move.
 * A booking or an edit names the state with `fixed`; one that names none keeps
 * what the visit holds. Answers the column's next value, `undefined` where it
 * stays as it is, or the refusal a Fixed visit with no reason meets.
 */
export interface VisitFixedInput {
  fixed?: boolean | null;
  fixedReason?: string | null;
}

export function visitFixedReasonOf(input: VisitFixedInput | null | undefined): { value: string | null | undefined; problem: string | null } {
  if (!input || input.fixed === undefined || input.fixed === null) return { value: undefined, problem: null };
  if (!input.fixed) return { value: null, problem: null };
  const reason = input.fixedReason?.trim() ?? '';
  if (!reason) return { value: undefined, problem: 'a Fixed visit records why it cannot move' };
  return { value: reason, problem: null };
}

/** 🔒 Decision 3100: whether a visit is Fixed, read off its reason; an absent reason from an older reply reads Can move. */
export function visitIsFixed(visit: { fixedReason?: string | null }): boolean {
  return typeof visit.fixedReason === 'string' && visit.fixedReason.trim() !== '';
}

/**
 * 🔒 What Edit Visit sends (decision 2813): the window as wall clocks in the
 * zone, the whole crew and its one lead, and the visit's version as the page
 * read it. A move of the job's own booking also names why it moved and the
 * tier the warning showed, as the job's Reschedule always has.
 */
/**
 * 🔒 Decision 5571: the labor lines a booking names, the ones the visit
 * covers and against each worker the ones they are sent for, by line id and
 * employment id. Absent covers every line of the job with every worker on a new
 * visit, and keeps what an existing visit holds.
 */
export interface VisitLinesInput {
  visitLineIds?: string[] | null;
  workerLineIds?: Record<string, string[]> | null;
}

/** 🔒 Decision 5571: one labor line as a booking form offers it, its place on the job and its work. */
export interface VisitLineOption {
  lineId: string;
  label: string;
}

/**
 * 🔒 Decision 5571: what a booking form sends for its lines. Every line ticked
 * and every worker on every covered line sends nothing, so the default stays
 * the server's; anything narrower sends the lines and each worker's.
 */
export function visitLinesInput(all: readonly string[], covered: readonly string[] | null | undefined, byWorker: Readonly<Record<string, readonly string[]>> | null | undefined, crew: readonly string[]): VisitLinesInput {
  const lines = covered ?? all;
  const narrowed = lines.length !== all.length || crew.some((employmentId) => {
    const own = byWorker?.[employmentId];
    return own !== undefined && own.filter((id) => lines.includes(id)).length !== lines.length;
  });
  if (!narrowed) return {};
  const workerLineIds: Record<string, string[]> = {};
  for (const employmentId of crew) workerLineIds[employmentId] = (byWorker?.[employmentId] ?? lines).filter((id) => lines.includes(id));
  return { visitLineIds: [...lines], workerLineIds };
}

export interface EditVisitInput extends VisitFixedInput, VisitLinesInput {
  startsAt: string;
  endsAt?: string | null;
  timeZone: string;
  workerEmploymentIds?: string[] | null;
  leadEmploymentId?: string | null;
  version?: number | null;
  reason?: string | null;
  acknowledgedTierId?: string | null;
}

/** One person going on a visit, as the Visits tile draws them. */
export interface WorkOrderVisitPerson {
  employmentId: string;
  personId: string;
  name: string;
  isLead: boolean;
  /**
   * 🔒 Piece 29 (T3859): whether the person has opened the job on My Jobs, read from the seen record that marks a job New there
   * (decision 3072); absent from an older reply and from every read but the Visits tile's, which reads as opened.
   */
  jobOpened?: boolean;
  /** 🔒 When the person last opened My Jobs, as an instant; null where they never have. Only the Visits tile's read carries it. */
  myJobsOpenedAt?: string | null;
}

/** One row of the Visits tile (decision 1908). */
export interface WorkOrderVisitRow {
  id: string;
  version: number;
  visitNumber: number;
  /** V1, V2 and on. */
  label: string;
  windowStart: string;
  windowEnd: string | null;
  status: WorkOrderVisitStatus;
  outcome: 'Completed' | 'CouldNotComplete' | null;
  cancelReason: string | null;
  lead: WorkOrderVisitPerson | null;
  crew: WorkOrderVisitPerson[];
  /** 🔒 Whether the visit carries the job's own booking, whose move re-reads the tier and records why (decision 2813); absent reads as no. */
  carriesBooking?: boolean;
  /** 🔒 Decision 3100: why the visit cannot move, which reads it Fixed; null or absent reads Can move. */
  fixedReason?: string | null;
  /** 🔒 Decision 2910: the visit's hours, its notes and its report's number and state; absent from an older reply. */
  hours?: number;
  noteCount?: number;
  reportNumber?: string;
  reportState?: 'Open' | 'Signed' | 'Sent' | 'Void' | null;
  /** 🔒 Decision 5571: the lines the visit covers, and each worker's by employment; absent from an older reply. */
  lineIds?: string[];
  workerLineIds?: Record<string, string[]>;
}

/**
 * 🔒 Piece 29 (T3859): the crew of a visit still to come who have not opened the job on My Jobs, in the crew's order.
 * A Complete or Cancelled visit names nobody, and a crew member the read gives no answer for reads as opened.
 */
export function crewNotYetOpened(visit: Pick<WorkOrderVisitRow, 'status' | 'crew'>): WorkOrderVisitPerson[] {
  if (!visitIsOpen(visit.status)) return [];
  return visit.crew.filter((person) => person.jobOpened === false);
}

/** What the Visits tile reads (decision 1908; T1201). */
export interface WorkOrderVisitsAnswer {
  workOrderId: string;
  visits: WorkOrderVisitRow[];
  /** Whether Book Another Visit stands on this job for this viewer. */
  canBook: boolean;
  /** 🔒 Decision 5570: whether the tile books a Draft job's first visit inline, through the job's own booking; absent reads as no. */
  canBookFirst?: boolean;
  /** Whether a row offers Cancel for this viewer. */
  canCancel: boolean;
  /** 🔒 Whether a visit still open offers Edit Visit for this viewer (decision 2813); absent reads as no. */
  canEdit?: boolean;
  /** 🔒 Decision 12970: whether a ticked visit offers Delete Anyway, true for the `admin.operations` holder alone; absent reads as no. */
  canDeleteAnyway?: boolean;
  /** 🔒 Piece 29 (T1710): the job type's estimated minutes, the booking window's default length; null or absent where the job names none. */
  jobTypeMinutes?: number | null;
  /** 🔒 Decision 5571: the job's labor lines, which a booking form ticks; absent from an older reply. */
  lines?: VisitLineOption[];
}
